<template>
  <leaflet-map
    ref="fullMap"
    style="height: 80vh;"
    fit-once
    :geolocate="false"
  >
    <template #controls-topright>
      <div v-if="withTracking && shipping.courier_id" class="mt-2 leaflet-control-layers leaflet-control-layers-expanded">
        <b-form-checkbox v-model="showTracking">
          <b-spinner small v-if="trackingWorking"></b-spinner>
          <translate>Show tracking</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
          <translate>Show heathPath</translate>
        </b-form-checkbox>
      </div>
    </template>
  </leaflet-map>
</template>

<script>
import {
  TrackingService,
  ShippingService
} from '@/services/api'
import ShippingsMixin from '@/mixins/shippings.mixin'
import LeafletMap from '@/components/Maps/LeafletMap'

export default {
  name: 'ShippingMap',
  components: {
    LeafletMap
  },
  props: {
    shippingId: {
      type: String,
      default: '',
      required: true
    },
    withTracking: {
      type: Boolean,
      default: false
    },
    popupType: {
      type: String,
      default: 'shipping'
    },
    updateEvery: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      shipping: {},
      markers: [],
      polylines: [],
      tracking: [],
      heathPath: [],
      showTracking: false,
      showHeathPath: false,
      trackingWorking: false,
      fetchTimeout: null
    }
  },
  watch: {
    markers(val) {
      setTimeout(function () {
        this.$refs.fullMap.addMarkers(val)
        this.$refs.fullMap.addPolylines(this.polylines)
      }.bind(this, val), 500)
    },
    tracking(val) {
      setTimeout(function () {
        this.$refs.fullMap.addTracking(val)
      }.bind(this, val), 500)
      if (val.length === 0) {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    heathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    showTracking(val) {
      if (val) {
        const from = new Date(this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at)
        const to = new Date(this.shipping.status_history.findIndex(x => x.status === 'DELIVERED') !== -1 ? this.shipping.status_history.find(x => x.status === 'DELIVERED').created_at : this.moment.utc(this.shipping.status_history[0].created_at).format('YYYY-MM-DD HH:mm:ss'))
        let hours = (to.getTime() - from.getTime()) / 1000 / 60 / 60 / 24

        if (hours <= 1) {
          this.tracking = this.fetchTracking(this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at, this.shipping.status_history.findIndex(x => x.status === 'DELIVERED') !== -1 ? this.shipping.status_history.find(x => x.status === 'DELIVERED').created_at : this.moment.utc(this.shipping.status_history[0].created_at).format('YYYY-MM-DD HH:mm:ss'))
        } else {
          let trackingFetchesArray = []
          let i = 0
          while (hours > 0) {
            const toAdd = hours < 1 ? hours : 1
            trackingFetchesArray.push(this.fetchTracking(this.moment.utc(this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at).add(i * 24, 'hours').format('YYYY-MM-DD HH:mm:ss'), this.moment.utc(this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at).add((i + toAdd) * 24, 'hours').format('YYYY-MM-DD HH:mm:ss')))
            hours--
            i++
          }
          Promise.all(trackingFetchesArray)
            .then(results => {
              let tracking = []
              for (const array of results) {
                tracking = tracking.concat(array)
              }
              this.tracking = tracking.sort((a, b) => a.created_at - b.created_at)
            })
        }
      } else {
        this.tracking = []
        this.showHeathPath = false
      }
    },
    showHeathPath(val) {
      if (val) {
        this.heathPath = this.tracking.map(x => {
          return [x.lat, x.lng, x.z]
        })
      } else {
        this.heathPath = []
      }
    }
  },
  mixins: [
    ShippingsMixin
  ],
  mounted() {
    this.fetchShipping()
  },
  beforeDestroy() {
    clearTimeout(this.fetchTimeout)
  },
  methods: {
    async fetchTracking(dateFrom, dateTo) {
      this.trackingWorking = true
      const params = {
        page: 0,
        limit: 1,
        date_from: dateFrom,
        date_to: dateTo,
        filter_groups: [
          {
            filters: [
              ['shipping_id', 'eq', this.shipping.id]
            ]
          }
        ]
      }

      return await TrackingService.getAll({ params })
        .then(response => {
          let markers = []

          const uniqueTrackingPointsByGeo = response.body.data.filter((thing, index, self) =>
            index === self.findIndex((t) => {
              return thing.lat === t.lat && thing.lng === t.lng
            })
          )
          this.trackingWorking = false

          let timeThreshold = null
          uniqueTrackingPointsByGeo.forEach((item, i) => {
            if (timeThreshold === null || Number.parseInt(item.created_at.replaceAll(/\D/g, '')) > timeThreshold) {
              markers.push({
                id: 1000 + i,
                lat: item.lat,
                lng: item.lng,
                icon: {
                  type: 'marker-circle',
                  size: [10, 10],
                  anchor: [5, 5],
                  color: '#000',
                  text: (i + 1)
                },
                x: 11,
                y: 34,
                infoText: (i + 1) + ': ' + this.moment.utc(item.created_at).tz(this.$tz).format('L LT'),
                tooltip: true,
                z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
              })
              timeThreshold = Number.parseInt(item.created_at.replaceAll(/\D/g, '')) + 10
            }
          })
          this.tracking = markers
          return markers
        })
    },
    fetchShipping() {
      if (this.updateEvery) {
        clearTimeout(this.fetchTimeout)
      }
      let params = {
        includes: [
          'courier.courier_available.tracking',
          'status_history',
          'items.item_tracking',
          'items.location',
          'items.last_known_location'
        ]
      }
      ShippingService.get(this.shippingId, {params})
        .then(response => {
          this.shipping = response.body
          this.setMapData()
          if (this.updateEvery) {
            this.fetchTimeout = setTimeout(() => {
              this.fetchShipping()
            }, this.updateEvery * 1000)
          }
        })
    },
    setMapData() {
      let icon = ''
      let markers = []

      let infoWindowHtml = this.formatShippingPopup(this.shipping)

      markers.push({
        id: 1,
        lat: this.shipping.address_from.lat || this.shipping.from_lat,
        lng: this.shipping.address_from.lng || this.shipping.from_lng,
        icon: {
          type: 'map-marker',
          size: [27, 36],
          anchor: [18, 39],
          color: '#999',
          text: '↑'
        },
        x: 11,
        y: 34,
        infoText: {
          id: this.shipping.id,
          type: 'shipping',
          html: infoWindowHtml,
          data: this.shipping,
          ids: [this.shipping.id]
        },
        popup: true
      })
      markers.push({
        id: 2,
        lat: this.shipping.address_to.lat || this.shipping.to_lat,
        lng: this.shipping.address_to.lng || this.shipping.to_lng,
        icon: {
          type: 'map-marker',
          size: [27, 36],
          anchor: [18, 39],
          color: '#999',
          text: '↓'
        },
        x: 11,
        y: 34,
        infoText: {
          id: this.shipping.id,
          type: 'shipping',
          html: infoWindowHtml,
          data: this.shipping,
          ids: [this.shipping.id]
        },
        popup: true
      })

      let data = {
        line: {
          polyLine: markers.filter(x => x.infoText.type !== 'courier'),
          polyLineOptions: {
            strokeColor: '#333',
            strokeWeight: 1
          }
        },
        markers: markers
      }

      if (this.shipping.courier && this.shipping.courier.courier_available && this.shipping.courier.courier_available.tracking) {
        if (this.shipping.courier.courier_available.tracking[0]) {
          icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === this.shipping.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'

          markers.push({
            id: 1000,
            lat: this.shipping.courier.courier_available.tracking[0].lat,
            lng: this.shipping.courier.courier_available.tracking[0].lng,
            icon: icon,
            x: 25,
            y: 25,
            infoText: {id: this.shipping.courier.id, type: 'courier', user: this.shipping.courier},
            popup: true
          })
        }
      }

      if (this.shipping.items[0]?.item_tracking?.last_position_updated_at) {
        let item = this.shipping.items[0]
        let infoWindowHtml = ''
        infoWindowHtml += item.reference_code
        let itemColor
        if (this.ecosystemModules.items.icon.colorByMinAge) {
          const daysIdle = this.elapsedTimeRaw(item.item_tracking.last_position_updated_at) / 86400000
          itemColor = this.itemColorByAge(daysIdle, 'items')
        }

        let direction = 0
        if (this.ecosystemModules.items.directionableBy) {
          direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
        }

        let dynamic = null
        try {
          if (this.ecosystemModules.items.dynamicableBy) {
            dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item))
          }
        } catch (e) {
          console.log(e)
        }

        let load = null
        try {
          if (this.ecosystemModules.items.loadableBy) {
            load = this.ecosystemModules.items.loadableBy.split('.').reduce((o, i) => o[i], item)
          }
        } catch (e) {
          console.assert(e)
        }

        markers.push({
          id: 100000,
          cardType: 'item',
          lat: item[this.ecosystemModules.items.geolocalizableBy].lat || item.lat,
          lng: item[this.ecosystemModules.items.geolocalizableBy].lng || item.lng,
          icon: {
            type: this.ecosystemModules.items.icon.type,
            size: this.ecosystemModules.items.icon.size,
            anchor: this.ecosystemModules.items.icon.anchor,
            popupAnchor: this.ecosystemModules.items.icon.popupAnchor,
            color: itemColor,
            text: this.ecosystemModules.items.icon.text || item.reference_code,
            direction,
            dynamic,
            load
          },
          x: 30,
          y: 30,
          optimized: false,
          infoText: {
            id: item.id,
            type: 'item-id',
            html: infoWindowHtml,
            color: itemColor,
            data: item,
            isShared: item.isShared || false
          },
          popup: true
        })
      }

      this.markers = markers
      this.polylines = [data.line]
    }
  }
}
</script>
